import React from "react";
import { MapPin, Car, Train } from "lucide-react";
import { PARK } from "@/lib/site";

const nearby = [
  { name: "RSPB Frampton Marsh", distance: "10 min drive", detail: "Bird reserve & wetlands" },
  { name: "Boston Stump (St Botolph's)", distance: "15 min drive", detail: "Medieval church & views" },
  { name: "Moulton Marsh Nature Reserve", distance: "12 min drive", detail: "Coastal walking" },
  { name: "Sutterton Village", distance: "15 min walk", detail: "Shops, pubs, post office" },
];

const Location = () => {
  return (
    <section
      id="location"
      data-testid="location-section"
      className="py-24 md:py-36 bg-[#0F0E0E] text-[#EAE0D0] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="overline text-[#E4B2A8]">
              <span
                className="marker-line"
                style={{ background: "#B76E79" }}
              />
              Where to find us
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-[1.05] text-[#F5EFE6]">
              Between the fens and the sea.
            </h2>
            <p className="mt-6 text-[#D8CDBA] text-base md:text-lg leading-relaxed">
              Walnut Lakes is a short hop from the A16/A17 roundabout — quiet
              lanes on one side, a well-served village on the other, and the
              Lincolnshire coast just over the horizon.
            </p>

            <div className="mt-10 space-y-5 text-[#EFE7DC]">
              <div className="flex items-start gap-4" data-testid="location-address">
                <MapPin size={18} className="mt-1 text-[#E4B2A8]" strokeWidth={1.5} />
                <div>
                  {PARK.address.lines.map((l) => (
                    <div key={l} className="text-sm md:text-base">{l}</div>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Car size={18} className="mt-1 text-[#E4B2A8]" strokeWidth={1.5} />
                <div className="text-sm md:text-base">
                  Just off the A16 / A17 roundabout · Free on-site parking
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Train size={18} className="mt-1 text-[#E4B2A8]" strokeWidth={1.5} />
                <div className="text-sm md:text-base">
                  Bus links to Boston &amp; Spalding from the village
                </div>
              </div>
            </div>

            <div className="mt-10">
              <a
                href="https://www.google.com/maps/search/?api=1&query=Walnut+Lakes+Main+Road+PE20+2LQ"
                target="_blank"
                rel="noreferrer"
                data-testid="location-directions-cta"
                className="btn-ghost-light"
              >
                Get directions
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[16/12] shadow-lg">
              <iframe
                title="Walnut Lakes location map"
                src="https://www.google.com/maps?q=Walnut%20Lakes%20Main%20Road%20PE20%202LQ&t=&z=13&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.15) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {nearby.map((n) => (
                <div
                  key={n.name}
                  data-testid={`nearby-${n.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="p-5 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm"
                >
                  <div className="text-[#F5EFE6] text-base font-medium">{n.name}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#E4B2A8]">
                    {n.distance}
                  </div>
                  <div className="mt-2 text-sm text-[#D8CDBA]">{n.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
